/**
 * Device-registry inspection checklist — single surface for technician + admin parity.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../layout/AppShell';
import TechnicianBuildingDevicesBlock from '../technician/TechnicianBuildingDevicesBlock';
import FieldDeviceSectionPanel, { type FieldDeviceRecord } from './FieldDeviceSectionPanel';
import BatteryLoadCalculator, { type BatteryLoadSheet } from './BatteryLoadCalculator';
import type { ChecklistPickerDeviceRow } from './ChecklistDevicePicker';
import { fieldSectionIsComplete, isFieldDeviceSection, matchingDevicesForFieldSection } from '../../lib/fieldDeviceSections';
import { isEmergencyPowerSection } from '../../lib/battery-load-calc';
import { apiRequest, buildApiUrl } from '../../api/client';
import '../../pages/admin/quotes/quote-generator.css';
import './inspection-runner.css';

type DeviceSnap = {
    type?: string;
    manufacturer?: string;
    modelName?: string;
    modelNumber?: string;
    serialNumber?: string;
    installationLocation?: string;
    floor?: string;
    zone?: string;
    capturedAt?: string;
};

type DevicePopulated = {
    _id?: string;
    type?: string;
    manufacturer?: string;
    modelName?: string;
    serialNumber?: string;
    installationLocation?: string;
    floor?: string;
    zone?: string;
};

type InspectionItem = {
    itemId: string;
    category: string;
    label: string;
    required?: boolean;
    result: 'pass' | 'fail' | 'deficient' | 'n/a' | null;
    note?: string;
    photoUrl?: string;
    photos?: string[];
    deviceId?: string | DevicePopulated | null;
    deviceSnapshot?: DeviceSnap | null;
    deficiencyId?: string | { _id?: string; description?: string; severity?: string } | null;
};

type Inspection = {
    _id: string;
    siteAddress?: string;
    status: 'in_progress' | 'completed';
    items: InspectionItem[];
    clientId?: string | { _id?: string; firstName?: string; lastName?: string; buildingName?: string } | null;
    technicianId?: { name?: string } | null;
    frequency?: string;
    standardCode?: string;
    isNewBuilding?: boolean;
    devicesRegisteredDuringVisit?: number;
    deviceDriven?: boolean;
    fieldDeviceRecords?: FieldDeviceRecord[];
    annualSheets?: { batteryLoad?: BatteryLoadSheet };
};

type BuildingStatusPayload = {
    isNewBuilding: boolean;
    deviceCount: number;
};

type HistoricalInspectionRow = {
    _id: string;
    status?: 'in_progress' | 'completed';
    createdAt?: string;
    updatedAt?: string;
    frequency?: string;
    standardCode?: string;
    siteAddress?: string;
};

type Props = { basePath: string; backPath?: string };

function extractClientOid(inv: Inspection): string | null {
    const c = inv.clientId;
    if (!c) return null;
    if (typeof c === 'string' && c.trim()) return c.trim();
    if (typeof c === 'object' && '_id' in c && (c as { _id?: unknown })._id != null) return String((c as { _id: string })._id);
    return null;
}

function deviceGroupKey(it: InspectionItem): string | null {
    const raw = it.deviceId;
    if (!raw) return null;
    if (typeof raw === 'string') return raw;
    if (typeof raw === 'object' && raw !== null && '_id' in raw && (raw as { _id?: string })._id) {
        return String((raw as { _id: string })._id);
    }
    return null;
}

function categoryGroupKey(it: InspectionItem): string {
    const cat = (it.category || '').trim();
    return cat || 'General';
}

async function uploadFile(file: File): Promise<string | null> {
    const fd = new FormData();
    fd.append('file', file);
    const response = await fetch(buildApiUrl('/api/upload'), { method: 'POST', body: fd });
    const result = await response.json();
    return result.success && result.data?.url ? (result.data.url as string) : null;
}

export default function DeviceDrivenInspectionChecklist({ basePath, backPath }: Props): React.JSX.Element {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const listPath = backPath ?? basePath;
    const [inspection, setInspection] = useState<Inspection | null>(null);
    const [status, setStatus] = useState<BuildingStatusPayload | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [completing, setCompleting] = useState(false);
    const [deficiencyDraft, setDeficiencyDraft] = useState<
        Record<
            string,
            { description: string; severity: 'critical' | 'major' | 'minor'; saving?: boolean; open?: boolean }
        >
    >({});
    const [activeSection, setActiveSection] = useState<'checklist' | 'devices' | 'reports'>('checklist');
    const [checklistSectionIndex, setChecklistSectionIndex] = useState(0);
    const [historyBusy, setHistoryBusy] = useState(false);
    const [historyRows, setHistoryRows] = useState<HistoricalInspectionRow[]>([]);
    const [historyError, setHistoryError] = useState<string | null>(null);
    const [devicesCatalog, setDevicesCatalog] = useState<ChecklistPickerDeviceRow[]>([]);

    const load = useCallback(() => {
        if (!id) return;
        setBusy(true);
        setErrorMessage(null);
        Promise.all([
            apiRequest<{ success?: boolean; data: Inspection }>(`/api/inspections/${id}`),
            apiRequest<{ success?: boolean; data: BuildingStatusPayload }>(`/api/inspections/${id}/building-status`)
        ])
            .then(([inspRow, bs]) => {
                const row = inspRow.data;
                setInspection(row);
                setStatus(bs.data);
                if (row.status === 'completed') {
                    navigate(`${basePath}/${id}/summary`, { replace: true });
                }
            })
            .catch((e: unknown) => {
                setErrorMessage(e instanceof Error ? e.message : 'Failed to load inspection.');
            })
            .finally(() => setBusy(false));
    }, [id, basePath, navigate]);

    useEffect(() => {
        load();
    }, [load]);

    const clientOid = inspection ? extractClientOid(inspection) : null;
    const showReportsTab = !(status?.isNewBuilding || inspection?.isNewBuilding);
    const tabs: Array<{ key: 'checklist' | 'devices' | 'reports'; label: string }> = [
        { key: 'checklist', label: 'Checklist' },
        { key: 'devices', label: 'Building devices' },
        ...(showReportsTab ? [{ key: 'reports' as const, label: 'Older reports' }] : [])
    ];
    const activeIndex = Math.max(
        0,
        tabs.findIndex((tab) => tab.key === activeSection)
    );
    const clientName = (() => {
        const c = inspection?.clientId;
        if (!c || typeof c === 'string') return '';
        return `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.buildingName || '';
    })();

    const groups = useMemo(() => {
        if (!inspection?.items) return [];
        const map = new Map<string, { header: string; items: InspectionItem[] }>();
        for (const it of inspection.items) {
            const k = categoryGroupKey(it);
            if (!map.has(k)) {
                map.set(k, { header: k, items: [] });
            }
            map.get(k)!.items.push(it);
        }
        return Array.from(map.entries()).map(([key, group]) => {
            return {
                key,
                header: group.header,
                snap: null,
                items: [...group.items].sort((a, b) => a.label.localeCompare(b.label))
            };
        });
    }, [inspection]);

    const counts = useMemo(() => {
        const items = inspection?.items ?? [];
        return {
            total: items.length,
            pass: items.filter((i) => i.result === 'pass').length,
            fail: items.filter((i) => i.result === 'fail' || i.result === 'deficient').length,
            na: items.filter((i) => i.result === 'n/a').length,
            remaining: items.filter((i) => !i.result).length,
            deficientFlags: items.filter((i) => i.deficiencyId).length
        };
    }, [inspection]);

    useEffect(() => {
        if (groups.length === 0) return;
        if (checklistSectionIndex >= groups.length) {
            setChecklistSectionIndex(groups.length - 1);
        }
    }, [groups.length, checklistSectionIndex]);

    useEffect(() => {
        if (activeSection !== 'checklist') return;
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [checklistSectionIndex, activeSection]);

    async function patchItem(itemId: string, patch: Partial<{ result: InspectionItem['result']; note: string; photos: string[] }>) {
        if (!id || !inspection) return;
        try {
            const res = await apiRequest<{ success: boolean; data: Inspection }>(`/api/inspections/${id}/items`, {
                method: 'PATCH',
                json: { items: [{ itemId, ...patch }] }
            });
            setInspection(res.data);
        } catch (e: unknown) {
            setErrorMessage(e instanceof Error ? e.message : 'Could not save line.');
        }
    }

    function setResult(itemId: string, result: 'pass' | 'fail' | 'n/a') {
        void patchItem(itemId, { result });
    }

    async function attachPhotos(itemId: string, files: FileList | null) {
        if (!files?.length || !inspection) return;
        const item = inspection.items.find((i) => i.itemId === itemId);
        if (!item) return;
        const urls: string[] = [...(item.photos || [])];
        for (let i = 0; i < files.length; i += 1) {
            const u = await uploadFile(files[i]);
            if (u) urls.push(u);
        }
        await patchItem(itemId, { photos: urls });
    }

    async function submitDeficiency(itemId: string) {
        const d = deficiencyDraft[itemId];
        if (!d?.description?.trim() || !id) return;
        const line = inspection?.items?.find((i) => i.itemId === itemId);
        if (!line) return;
        setDeficiencyDraft((prev) => ({ ...prev, [itemId]: { ...prev[itemId]!, saving: true } }));
        try {
            const devicePayload = deviceGroupKey(line);
            await apiRequest('/api/deficiencies', {
                method: 'POST',
                json: {
                    inspectionId: id,
                    checklistItemId: itemId,
                    description: d.description.trim(),
                    severity: d.severity,
                    photos: [],
                    deviceId: devicePayload || undefined
                }
            });
            setDeficiencyDraft((prev) => {
                const next = { ...prev };
                delete next[itemId];
                return next;
            });
            load();
        } catch (e: unknown) {
            setErrorMessage(e instanceof Error ? e.message : 'Could not flag deficiency.');
        } finally {
            setDeficiencyDraft((prev) => ({
                ...prev,
                ...(prev[itemId] ? { [itemId]: { ...prev[itemId]!, saving: false, open: true } } : {})
            }));
        }
    }

    async function completeInspection() {
        if (!id) return;
        setCompleting(true);
        try {
            await apiRequest(`/api/inspections/${id}/complete`, { method: 'PATCH' });
            navigate(`${basePath}/${id}/summary`, { state: backPath ? { backPath: listPath } : undefined });
        } catch (e: unknown) {
            setErrorMessage(e instanceof Error ? e.message : 'Could not complete inspection.');
        } finally {
            setCompleting(false);
        }
    }

    const loadOlderReports = useCallback(async () => {
        if (!clientOid || !id) return;
        setHistoryBusy(true);
        setHistoryError(null);
        try {
            const res = await apiRequest<{ success?: boolean; data: HistoricalInspectionRow[] }>(
                `/api/inspections?clientId=${encodeURIComponent(clientOid)}`
            );
            const rows = (Array.isArray(res.data) ? res.data : [])
                .filter((row) => row._id !== id)
                .filter((row) => String(row.status || '').toLowerCase() === 'completed')
                .sort((a, b) => {
                    const bt = new Date(b.createdAt || b.updatedAt || 0).getTime();
                    const at = new Date(a.createdAt || a.updatedAt || 0).getTime();
                    return bt - at;
                });
            setHistoryRows(rows);
        } catch (e: unknown) {
            setHistoryError(e instanceof Error ? e.message : 'Failed to load older reports.');
        } finally {
            setHistoryBusy(false);
        }
    }, [clientOid, id]);

    useEffect(() => {
        if (activeSection !== 'reports') return;
        if (status?.isNewBuilding || inspection?.isNewBuilding) return;
        void loadOlderReports();
    }, [activeSection, status?.isNewBuilding, inspection?.isNewBuilding, loadOlderReports]);

    useEffect(() => {
        if (!clientOid) {
            setDevicesCatalog([]);
            return;
        }
        apiRequest<{ success?: boolean; data: Array<ChecklistPickerDeviceRow & { _id?: string }> }>(
            `/api/devices?clientId=${encodeURIComponent(clientOid)}`
        )
            .then((res) => {
                const rows = Array.isArray(res.data) ? res.data : [];
                setDevicesCatalog(
                    rows
                        .filter((d) => d._id)
                        .map((d) => ({ ...d, _id: String(d._id) }))
                );
            })
            .catch(() => setDevicesCatalog([]));
    }, [clientOid, inspection?._id]);

    if (!id) {
        return (
            <AppShell>
                <div className="quote-page quote-container banner banner-error">Missing inspection id.</div>
            </AppShell>
        );
    }

    if (busy || !inspection || !status) {
        return (
            <AppShell>
                <div className="quote-page">
                    <div className="quote-container">
                        <p style={{ color: 'var(--dash-muted)' }}>Loading checklist…</p>
                    </div>
                </div>
            </AppShell>
        );
    }

    const allAnswered =
        counts.total > 0 &&
        inspection.items.every((i) => i.result !== null && i.result !== undefined && String(i.result).trim() !== '');

    const checklistSectionCount = groups.length;
    const safeChecklistSectionIndex =
        checklistSectionCount === 0 ? 0 : Math.min(checklistSectionIndex, checklistSectionCount - 1);
    const currentChecklistGroup = checklistSectionCount > 0 ? groups[safeChecklistSectionIndex] : null;
    const isFirstChecklistSection = safeChecklistSectionIndex === 0;
    const isLastChecklistSection = safeChecklistSectionIndex >= checklistSectionCount - 1;
    const completedChecklistSections = groups.filter((g) => {
        if (isFieldDeviceSection(g.header)) {
            const devicesDone = fieldSectionIsComplete(
                g.header,
                g.items.map((i) => i.label),
                g.items,
                devicesCatalog,
                inspection.fieldDeviceRecords || [],
                g.key
            );
            const itemsDone = g.items
                .filter((i) => i.itemId !== 'NEW-BUILDING-NOTICE')
                .every((i) => i.result);
            return devicesDone && itemsDone;
        }
        return g.items.every((i) => i.result);
    }).length;
    const fieldSectionIndexes = groups
        .map((g, index) => (isFieldDeviceSection(g.header) ? index : -1))
        .filter((index) => index >= 0);
    const onFieldDeviceSectionPage = currentChecklistGroup
        ? isFieldDeviceSection(currentChecklistGroup.header)
        : false;

    const goToPrevChecklistSection = () => {
        setChecklistSectionIndex((i) => Math.max(0, i - 1));
    };

    const goToNextChecklistSection = () => {
        setChecklistSectionIndex((i) => Math.min(checklistSectionCount - 1, i + 1));
    };

    return (
        <AppShell>
            <div className="ir-page">
                <div className="ir-wrap">

                    {errorMessage && (
                        <div className="ir-alert ir-alert--error">{errorMessage}</div>
                    )}

                    <div className="ir-header">
                        <div className="ir-header__left">
                            <button
                                type="button"
                                className="ir-back-link"
                                onClick={() => navigate(listPath)}
                            >
                                ← Inspections
                            </button>
                            <h1 className="ir-header__title">Inspection Checklist</h1>
                            {clientName && (
                                <p className="ir-header__meta">{clientName}</p>
                            )}
                            {inspection.siteAddress && (
                                <p className="ir-header__meta ir-header__meta--addr">
                                    {inspection.siteAddress}
                                </p>
                            )}
                        </div>
                        <div className="ir-header__stats">
                            <div className="ir-stat ir-stat--pass">
                                <span className="ir-stat__num">{counts.pass}</span>
                                <span className="ir-stat__lbl">Pass</span>
                            </div>
                            <div className="ir-stat ir-stat--fail">
                                <span className="ir-stat__num">{counts.fail}</span>
                                <span className="ir-stat__lbl">Fail</span>
                            </div>
                            <div className="ir-stat ir-stat--na">
                                <span className="ir-stat__num">{counts.na}</span>
                                <span className="ir-stat__lbl">N/A</span>
                            </div>
                            <div className="ir-stat ir-stat--rem">
                                <span className="ir-stat__num">{counts.remaining}</span>
                                <span className="ir-stat__lbl">Left</span>
                            </div>
                        </div>
                    </div>

                    <div className="ir-progress">
                        <div
                            className="ir-progress__fill"
                            style={{
                                width: counts.total > 0
                                    ? `${Math.round(((counts.pass + counts.fail + counts.na) / counts.total) * 100)}%`
                                    : '0%'
                            }}
                        />
                    </div>
                    <p className="ir-progress__label">
                        {counts.pass + counts.fail + counts.na} of {counts.total} items completed
                    </p>

                    {(status.isNewBuilding || inspection.isNewBuilding) && (
                        <div className="ir-alert ir-alert--warn">
                            <div className="ir-alert__icon">⚠️</div>
                            <div>
                                <p className="ir-alert__title">New Building — No Devices Registered</p>
                                <p className="ir-alert__body">
                                    No devices exist for this site yet. Complete the system-level sections
                                    above, then use the <strong>Building Devices</strong> tab to register
                                    all field devices. §23 line items will reflect the building once devices
                                    are added.
                                </p>
                            </div>
                        </div>
                    )}

                    <div role="tablist" aria-label="Inspection workflow" className="ir-tabs">
                        <div
                            className="ir-tabs__indicator"
                            style={{
                                width: `calc((100% - 8px) / ${tabs.length})`,
                                transform: `translateX(${activeIndex * 100}%)`
                            }}
                        />
                        {tabs.map((tab) => (
                            <button
                                key={tab.key}
                                type="button"
                                role="tab"
                                aria-selected={activeSection === tab.key}
                                onClick={() => setActiveSection(tab.key)}
                                className="ir-tabs__btn"
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {activeSection === 'checklist' && (
                        <>
                            {fieldSectionIndexes.length > 0 && !onFieldDeviceSectionPage ? (
                                <div className="ir-alert ir-alert--info">
                                    <div>
                                        <p className="ir-alert__title">Per-device inspection on field-device sections</p>
                                        <p className="ir-alert__body">
                                            Use <strong>Next</strong> to reach sections like §23 Field Devices, Smoke Detectors,
                                            or Fire Extinguishers — those pages include the searchable device picker and
                                            completion log.
                                            {fieldSectionIndexes[0] > safeChecklistSectionIndex ? (
                                                <>
                                                    {' '}
                                                    <button
                                                        type="button"
                                                        className="ir-jump-link"
                                                        onClick={() => setChecklistSectionIndex(fieldSectionIndexes[0])}
                                                    >
                                                        Jump to first field-device section →
                                                    </button>
                                                </>
                                            ) : null}
                                        </p>
                                    </div>
                                </div>
                            ) : null}

                            {checklistSectionCount > 0 ? (
                                <nav className="ir-section-pager" aria-label="Checklist section navigation">
                                    <button
                                        type="button"
                                        className="ir-section-pager__btn"
                                        onClick={goToPrevChecklistSection}
                                        disabled={isFirstChecklistSection}
                                    >
                                        ← Prev
                                    </button>
                                    <div className="ir-section-pager__meta">
                                        <span className="ir-section-pager__count">
                                            Section {safeChecklistSectionIndex + 1} of {checklistSectionCount}
                                        </span>
                                        <span className="ir-section-pager__title">
                                            {currentChecklistGroup?.header}
                                        </span>
                                        <span className="ir-section-pager__progress">
                                            {completedChecklistSections} of {checklistSectionCount} sections complete
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className="ir-section-pager__btn ir-section-pager__btn--next"
                                        onClick={goToNextChecklistSection}
                                        disabled={isLastChecklistSection}
                                    >
                                        Next →
                                    </button>
                                </nav>
                            ) : null}

                            {currentChecklistGroup ? (() => {
                                const g = currentChecklistGroup;
                                const isFieldSection = isFieldDeviceSection(g.header);
                                const isBatterySection = isEmergencyPowerSection(g.header);
                                const sectionDeviceLogs = (inspection.fieldDeviceRecords || []).filter(
                                    (r) => r.sectionKey === g.key
                                );
                                const requiredDeviceCount = matchingDevicesForFieldSection(
                                    g.header,
                                    g.items.map((i) => i.label),
                                    devicesCatalog
                                ).length;
                                const groupPassed = g.items.filter((i) => i.result === 'pass').length;
                                const groupTotal = g.items.length;
                                const groupComplete = isFieldSection
                                    ? fieldSectionIsComplete(
                                          g.header,
                                          g.items.map((i) => i.label),
                                          g.items,
                                          devicesCatalog,
                                          inspection.fieldDeviceRecords || [],
                                          g.key
                                      ) &&
                                      g.items
                                          .filter((i) => i.itemId !== 'NEW-BUILDING-NOTICE')
                                          .every((i) => i.result)
                                    : g.items.every((i) => i.result);
                                return (
                                    <div
                                        key={g.key}
                                        className={`ir-section${isFieldSection ? ' ir-section--field-devices' : ''}`}
                                    >
                                        <div className="ir-section__head">
                                            <div className="ir-section__head-left">
                                                <h2 className="ir-section__title">{g.header}</h2>
                                                <span className="ir-section__count">
                                                    {isFieldSection
                                                        ? `${sectionDeviceLogs.length}/${requiredDeviceCount} devices`
                                                        : `${groupPassed}/${groupTotal}`}
                                                </span>
                                            </div>
                                            {groupComplete && (
                                                <span className="ir-section__done">✓ Done</span>
                                            )}
                                        </div>

                                        {isBatterySection ? (
                                            <BatteryLoadCalculator
                                                key={`eps-${inspection._id}-${inspection.annualSheets?.batteryLoad ? 'saved' : 'new'}`}
                                                inspectionId={inspection._id}
                                                initial={inspection.annualSheets?.batteryLoad}
                                                disabled={inspection.status === 'completed'}
                                                onSaved={(_sheet, row) => setInspection(row as Inspection)}
                                                onError={(m) => setErrorMessage(m)}
                                            />
                                        ) : null}

                                        {isFieldSection ? (
                                            <div className="ir-section__field-panel">
                                                <FieldDeviceSectionPanel
                                                    inspectionId={inspection._id}
                                                    sectionKey={g.key}
                                                    sectionTitle={g.header}
                                                    sectionItems={g.items.map((item) => ({
                                                        itemId: item.itemId,
                                                        label: item.label
                                                    }))}
                                                    devices={devicesCatalog}
                                                    records={inspection.fieldDeviceRecords || []}
                                                    disabledReason={
                                                        !clientOid
                                                            ? 'Link this inspection to a client before selecting devices.'
                                                            : null
                                                    }
                                                    onInspectionUpdated={(row) => setInspection(row as Inspection)}
                                                    onError={(m) => setErrorMessage(m)}
                                                />
                                            </div>
                                        ) : null}

                                        {(!isFieldSection || requiredDeviceCount === 0)
                                            ? g.items.map((item) => {
                                            const deficiencyDoc =
                                                item.deficiencyId && typeof item.deficiencyId === 'object'
                                                    ? (item.deficiencyId as { _id?: string; severity?: string; description?: string })
                                                    : null;
                                            const deficientBadge = !!(deficiencyDoc?._id);
                                            const isNotice = item.itemId === 'NEW-BUILDING-NOTICE';

                                            if (isNotice) {
                                                return (
                                                    <div key={item.itemId} className="ir-notice">
                                                        <span className="ir-notice__icon">ℹ️</span>
                                                        <p className="ir-notice__text">{item.label}</p>
                                                    </div>
                                                );
                                            }

                                            return (
                                                <div
                                                    key={item.itemId}
                                                    className={[
                                                        'ir-item',
                                                        item.result === 'pass' ? 'ir-item--pass' : '',
                                                        item.result === 'fail' || item.result === 'deficient' ? 'ir-item--fail' : '',
                                                        item.result === 'n/a' ? 'ir-item--na' : ''
                                                    ].filter(Boolean).join(' ')}
                                                >
                                                    <div className="ir-item__strip" />

                                                    <div className="ir-item__body">
                                                        <div className="ir-item__label-row">
                                                            <p className="ir-item__label">{item.label}</p>
                                                            {deficientBadge && (
                                                                <span className="ir-badge ir-badge--deficient">Deficiency logged</span>
                                                            )}
                                                        </div>

                                                        {deficientBadge && deficiencyDoc && (
                                                            <div className="ir-item__def-summary">
                                                                <span className={`ir-badge ir-badge--sev-${deficiencyDoc.severity}`}>
                                                                    {String(deficiencyDoc.severity || '').toUpperCase()}
                                                                </span>
                                                                <span className="ir-item__def-desc">
                                                                    {(deficiencyDoc.description || '').slice(0, 140)}
                                                                </span>
                                                            </div>
                                                        )}

                                                        <div className="ir-result-row" role="radiogroup" aria-label="Result">
                                                            {(['pass', 'fail', 'n/a'] as const).map((r) => {
                                                                const active = r === 'n/a' ? item.result === 'n/a' : item.result === r;
                                                                return (
                                                                    <button
                                                                        key={r}
                                                                        type="button"
                                                                        role="radio"
                                                                        aria-checked={active}
                                                                        onClick={() => setResult(item.itemId, r)}
                                                                        className={[
                                                                            'ir-result-btn',
                                                                            `ir-result-btn--${r === 'n/a' ? 'na' : r}`,
                                                                            active ? 'ir-result-btn--active' : ''
                                                                        ].filter(Boolean).join(' ')}
                                                                    >
                                                                        {r === 'pass' && <span className="ir-result-btn__icon">✓</span>}
                                                                        {r === 'fail' && <span className="ir-result-btn__icon">✗</span>}
                                                                        {r === 'n/a' && <span className="ir-result-btn__icon">—</span>}
                                                                        <span>{r === 'n/a' ? 'N/A' : r.charAt(0).toUpperCase() + r.slice(1)}</span>
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>

                                                        <textarea
                                                            key={`${item.itemId}-note`}
                                                            className="ir-notes"
                                                            rows={2}
                                                            placeholder="Add a note…"
                                                            defaultValue={item.note ?? ''}
                                                            onBlur={(e) => void patchItem(item.itemId, { note: e.target.value })}
                                                        />

                                                        <div className="ir-photos">
                                                            <div className="ir-photos__actions">
                                                                <label htmlFor={`ph-cam-${item.itemId}`} className="ir-photos__btn">
                                                                    <span>📷</span>
                                                                    <span>Camera</span>
                                                                    <input
                                                                        id={`ph-cam-${item.itemId}`}
                                                                        className="ir-hidden-input"
                                                                        type="file"
                                                                        accept="image/*"
                                                                        capture="environment"
                                                                        onChange={(e) =>
                                                                            void attachPhotos(item.itemId, e.target.files).finally(() => {
                                                                                e.target.value = '';
                                                                            })
                                                                        }
                                                                    />
                                                                </label>
                                                                <label htmlFor={`ph-lib-${item.itemId}`} className="ir-photos__btn">
                                                                    <span>🖼️</span>
                                                                    <span>Library</span>
                                                                    <input
                                                                        id={`ph-lib-${item.itemId}`}
                                                                        className="ir-hidden-input"
                                                                        type="file"
                                                                        accept="image/*"
                                                                        multiple
                                                                        onChange={(e) =>
                                                                            void attachPhotos(item.itemId, e.target.files).finally(() => {
                                                                                e.target.value = '';
                                                                            })
                                                                        }
                                                                    />
                                                                </label>
                                                            </div>
                                                            {item.photos && item.photos.length > 0 && (
                                                                <div className="ir-photo-grid">
                                                                    {item.photos.map((url) => (
                                                                        <a
                                                                            key={url}
                                                                            href={url}
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                            className="ir-photo-thumb"
                                                                        >
                                                                            <img src={url} alt="" />
                                                                        </a>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {(item.result === 'fail' || item.result === 'deficient') && !deficientBadge && (
                                                            <div className="ir-def-zone">
                                                                {!deficiencyDraft[item.itemId]?.open ? (
                                                                    <button
                                                                        type="button"
                                                                        className="ir-def-trigger"
                                                                        onClick={() =>
                                                                            setDeficiencyDraft((p) => ({
                                                                                ...p,
                                                                                [item.itemId]: {
                                                                                    description: `${g.header} · ${item.label} failed.`,
                                                                                    severity: p[item.itemId]?.severity ?? 'major',
                                                                                    open: true,
                                                                                    saving: false
                                                                                }
                                                                            }))
                                                                        }
                                                                    >
                                                                        + Log deficiency
                                                                    </button>
                                                                ) : (
                                                                    <div className="ir-def-form">
                                                                        <p className="ir-def-form__label">Deficiency details</p>
                                                                        <textarea
                                                                            className="ir-notes ir-def-form__textarea"
                                                                            rows={3}
                                                                            value={deficiencyDraft[item.itemId]?.description ?? ''}
                                                                            onChange={(e) =>
                                                                                setDeficiencyDraft((prev) => ({
                                                                                    ...prev,
                                                                                    [item.itemId]: {
                                                                                        ...prev[item.itemId]!,
                                                                                        description: e.target.value,
                                                                                        severity: prev[item.itemId]?.severity ?? 'major'
                                                                                    }
                                                                                }))
                                                                            }
                                                                        />
                                                                        <div className="ir-def-form__row">
                                                                            <select
                                                                                className="ir-def-form__select"
                                                                                value={deficiencyDraft[item.itemId]?.severity ?? 'major'}
                                                                                onChange={(e) =>
                                                                                    setDeficiencyDraft((prev) => ({
                                                                                        ...prev,
                                                                                        [item.itemId]: {
                                                                                            ...prev[item.itemId]!,
                                                                                            severity: e.target.value as 'critical' | 'major' | 'minor'
                                                                                        }
                                                                                    }))
                                                                                }
                                                                            >
                                                                                <option value="critical">🔴 Critical</option>
                                                                                <option value="major">🟠 Major</option>
                                                                                <option value="minor">🟡 Minor</option>
                                                                            </select>
                                                                            <button
                                                                                type="button"
                                                                                className="ir-def-form__save"
                                                                                disabled={!!deficiencyDraft[item.itemId]?.saving}
                                                                                onClick={() => void submitDeficiency(item.itemId)}
                                                                            >
                                                                                {deficiencyDraft[item.itemId]?.saving ? 'Saving…' : 'Save deficiency'}
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })
                                            : null}
                                    </div>
                                );
                            })() : null}

                            {checklistSectionCount > 0 ? (
                                <nav
                                    className="ir-section-pager ir-section-pager--bottom"
                                    aria-label="Checklist section navigation"
                                >
                                    <button
                                        type="button"
                                        className="ir-section-pager__btn"
                                        onClick={goToPrevChecklistSection}
                                        disabled={isFirstChecklistSection}
                                    >
                                        ← Previous section
                                    </button>
                                    <span className="ir-section-pager__bottom-count">
                                        {safeChecklistSectionIndex + 1} / {checklistSectionCount}
                                    </span>
                                    {!isLastChecklistSection ? (
                                        <button
                                            type="button"
                                            className="ir-section-pager__btn ir-section-pager__btn--next"
                                            onClick={goToNextChecklistSection}
                                        >
                                            Next section →
                                        </button>
                                    ) : (
                                        <span className="ir-section-pager__last-label">Final section</span>
                                    )}
                                </nav>
                            ) : null}

                            {isLastChecklistSection ? (
                            <>
                            <div className="ir-summary">
                                <div className="ir-summary__grid">
                                    <div className="ir-summary__cell">
                                        <span className="ir-summary__val">{groups.length}</span>
                                        <span className="ir-summary__key">Sections</span>
                                    </div>
                                    <div className="ir-summary__cell">
                                        <span className="ir-summary__val ir-summary__val--pass">{counts.pass}</span>
                                        <span className="ir-summary__key">Passed</span>
                                    </div>
                                    <div className="ir-summary__cell">
                                        <span className="ir-summary__val ir-summary__val--fail">{counts.fail}</span>
                                        <span className="ir-summary__key">Failed</span>
                                    </div>
                                    <div className="ir-summary__cell">
                                        <span className="ir-summary__val">{counts.deficientFlags}</span>
                                        <span className="ir-summary__key">Deficiencies</span>
                                    </div>
                                </div>
                            </div>

                            <div className="ir-footer">
                                <button
                                    type="button"
                                    className="ir-footer__back"
                                    onClick={() => navigate(listPath)}
                                >
                                    ← Back
                                </button>
                                <button
                                    type="button"
                                    className="ir-footer__complete"
                                    onClick={() => void completeInspection()}
                                    disabled={!allAnswered || completing}
                                >
                                    {completing ? 'Completing…' : 'Complete Inspection'}
                                </button>
                            </div>
                            {!allAnswered && (
                                <p className="ir-footer__hint">
                                    All items must have a result before completing.
                                </p>
                            )}
                            </>
                            ) : null}
                        </>
                    )}

                    {activeSection === 'devices' && (
                        <>
                            {clientOid ? (
                                <TechnicianBuildingDevicesBlock
                                    clientId={clientOid}
                                    appendInspectionId={inspection.status === 'in_progress' ? inspection._id : null}
                                    onInspectionUpdated={(row) => setInspection(row as Inspection)}
                                    onError={(m) => setErrorMessage(m)}
                                />
                            ) : (
                                <div className="ir-alert ir-alert--error">
                                    This inspection needs a linked client to manage devices.
                                </div>
                            )}
                            <div className="ir-footer">
                                <button
                                    type="button"
                                    className="ir-footer__back"
                                    onClick={() => navigate(listPath)}
                                >
                                    ← Back to inspections
                                </button>
                            </div>
                        </>
                    )}

                    {activeSection === 'reports' && (
                        <>
                            <div className="ir-section">
                                <div className="ir-section__head">
                                    <div className="ir-section__head-left">
                                        <h2 className="ir-section__title">Older reports</h2>
                                        <span className="ir-section__count">{historyRows.length} found</span>
                                    </div>
                                    <button
                                        type="button"
                                        className="ir-reports__refresh"
                                        onClick={() => void loadOlderReports()}
                                    >
                                        Refresh
                                    </button>
                                </div>

                                {historyBusy && (
                                    <p className="ir-muted">Loading reports…</p>
                                )}
                                {historyError && (
                                    <div className="ir-alert ir-alert--error">{historyError}</div>
                                )}
                                {!historyBusy && !historyError && historyRows.length === 0 && (
                                    <p className="ir-muted">No completed reports found for this building.</p>
                                )}
                                {!historyBusy && !historyError && historyRows.length > 0 && (
                                    <div className="ir-report-list">
                                        {historyRows.map((row) => (
                                            <button
                                                key={row._id}
                                                type="button"
                                                className="ir-report-row"
                                                onClick={() => navigate(`${basePath}/${row._id}/summary`)}
                                            >
                                                <div className="ir-report-row__left">
                                                    <span className="ir-report-row__id">
                                                        #{row._id.slice(-6).toUpperCase()}
                                                    </span>
                                                    <span className="ir-report-row__meta">
                                                        {row.frequency || 'annual'} · {row.standardCode || 'ULC S536'}
                                                    </span>
                                                </div>
                                                <div className="ir-report-row__right">
                                                    <span className="ir-report-row__date">
                                                        {new Date(row.createdAt || row.updatedAt || Date.now()).toLocaleDateString()}
                                                    </span>
                                                    <span className="ir-report-row__arrow">→</span>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="ir-footer">
                                <button
                                    type="button"
                                    className="ir-footer__back"
                                    onClick={() => navigate(listPath)}
                                >
                                    ← Back to inspections
                                </button>
                            </div>
                        </>
                    )}

                </div>
            </div>
        </AppShell>
    );
}
